<template>
  <nx-fullscreen-dialog
    :active="active"
    :title="entity ? $t('message.wizard.new.' + entity) : ''"
    @dismiss="dismiss"
  >
    <template slot="toolbar-items">
      <v-btn
        v-if="!enableCreateAndContinue"
        @click="create(true)"
        color="accent"
        dark
        flat
        :loading="loading"
        :disabled="!flags.valid || loading"
      >{{ $t('message.wizard.createBtn') }}</v-btn>

      <template v-else>
        <v-btn
          v-if="features.CREATE_AND_CONTINUE_WIZARD_BUTTON"
          @click="create(false)"
          color="accent"
          dark
          flat
          :loading="loading"
          :disabled="!flags.valid || loading"
        >{{ $t('message.wizard.createAndContinueBtn') }}</v-btn>
        <v-btn
          @click="create"
          color="accent"
          dark
          flat
          :loading="loading"
          :disabled="!flags.valid || loading"
        >{{ editTextBtn }}</v-btn>
      </template>
    </template>

    <v-container py-3 fluid class="nx-taxonomy-wizard-body" v-if="active">
      <v-layout wrap fill-height>
        <v-flex class="md6 offset-md3 xs12">
          <nx-form-scope-generator
            v-if="config"
            ref="form"
            class="nx-form"
            :value="draft"
            @input="updateDraft"
            @form="flags = $event"
            @submit.stop.prevent
            :config="config"
            :title="config.title"
            :scope="validationScope"
            :hide-heading="hideScopeHeading"
            :validation-mode="config.validationMode"
            @trigger-population="handleTriggerPopulation"
          ></nx-form-scope-generator>
        </v-flex>
      </v-layout>
    </v-container>
  </nx-fullscreen-dialog>
</template>

<script>
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import NxFormScopeGenerator from '@/modules/form/FormScopeGenerator';
import Loadable from '@/mixins/loadable';
import TriggerPopulation from '@/modules/entity/mixins/entity-trigger-population';
import Toast from '@/modules/toast/services';
import { entityEditorRoute } from '@/modules/entity/router/index';
import { mapGetters, mapState } from 'vuex';
import apiFactory from '../api';
import wizardConfig from '@/modules/entity/wizard/config';
import uuid from 'uuid/v4';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'nx-config-entity-wizard',
  mixins: [Loadable, TriggerPopulation],
  components: {
    NxFormScopeGenerator,
    NxFullscreenDialog,
  },
  props: {
    // article, image, section, etc
    entity: {
      type: String,
      required: true,
    },
    // Si vale `true`, aparecerán dos botones: "Crear y continuar" y "Crear y editar".
    enableCreateAndContinue: {
      type: Boolean,
      default: () => false,
    },
    additionalPayload: {
      type: Object,
      default: (() => {}),
    },
    active: {
      type: Boolean,
      required: true,
    },
    config: {
      type: Object,
    },
    defaultValue: {
      type: Object,
    },
  },
  data() {
    return {
      flags: {},
      draft: {},
    };
  },
  computed: {
    ...mapState(['siteId']),
    ...mapGetters(['context', 'features']),
    editTextBtn() {
      if (this.features.CREATE_AND_CONTINUE_WIZARD_BUTTON) {
        return this.$t('message.wizard.createAndEditBtn', {
          type: this.$t(`message.wizard.${this.entity || 'default'}`),
        });
      }
      return this.$t('message.wizard.createBtn');
    },
    validationScope() {
      return `nx-config-wizard-${this.entity}-${uuid()}`;
    },
    hideScopeHeading() {
      // Si no hay título en la configuración, ocultamos la cabecera.
      return !this.config || !this.config.title;
    },
  },
  methods: {
    async updateDraft(field, value) {
      this.draft = { ...this.draft, ...{ [field]: value } };
    },
    async handleTriggerPopulation({ fieldConfig, field, value }) {
      const population = await this.triggerPopulation({ fieldConfig, field, value });
      this.draft = { ...this.draft, ...population };
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
    async create(navigateToEntityEditor = true) {
      const api = apiFactory(this.entity);
      this.loadingStart();
      try {
        const payload = { ...this.draft, ...this.additionalPayload, context: this.context };
        const payloadImg = new FormData();
        payloadImg.append('file', this.draft.file);
        payloadImg.append('payload', JSON.stringify(payload));
        // si estamos usando payloadImg no necesitamos la prop
        // file en el payload normal porq no lo vamos a usar para crear la imagen
        if (payload.file) {
          delete payload.file;
        }
        const response = await api.create(
          this.entity === 'image' ? payloadImg : payload,
        );
        if (!response) {
          Toast.notify('message.wizard.createError');
          this.loadingFinish();
          return;
        }

        const entity = {
          ...response,
          route: entityEditorRoute.get(this.entity, response),
          navigateToEntityEditor: Boolean(navigateToEntityEditor),
        };

        this.$emit('entityCreated', entity);
        Toast.notify('message.wizard.createSuccess');

        if (navigateToEntityEditor) {
          this.dismiss();
        } else {
          this.reset();
        }
      } catch (e) {
        console.log('Error creating entity...');
      } finally {
        this.loadingFinish();
      }
    },
    reset() {
      this.flags = {};
      this.draft = {};
    },
    dismiss() {
      this.reset();
      this.$emit('dismiss');
    },
  },
  watch: {
    active: {
      handler(active) {
        if (!active) {
          return;
        }
        if (this.defaultValue) {
          this.draft = cloneDeep(this.defaultValue);
        }
        wizardConfig.fillDraft({ wizard: this, draft: this.draft, router: this.$router });
      },
    },
  },
};
</script>
